/**
 * Indian microfinance interest: quoted as **rupees per ₹100 principal**
 * (not %). All loans use a **standard 30-day month** for daily ↔ monthly conversion.
 */
export const STANDARD_DAYS_PER_MONTH = 30;

export type InterestBasis = 'daily' | 'monthly';

export function parseInterestBasis(v: unknown): InterestBasis {
  return v === 'daily' ? 'daily' : 'monthly';
}

export function interestRateLabel(basis: InterestBasis): string {
  return basis === 'daily' ? 'Interest (₹ per ₹100 principal / day)' : 'Interest (₹ per ₹100 principal / month)';
}

export function daysInMonth(_year: number, _monthIndex: number): number {
  return STANDARD_DAYS_PER_MONTH;
}

export function daysInMonthForDate(_date = new Date()): number {
  return STANDARD_DAYS_PER_MONTH;
}

/** Standard month length: `monthCount × 30` days. */
export function totalDaysAcrossMonths(monthCount: number, _start = new Date()): number {
  const n = Math.max(0, Math.floor(monthCount));
  return n * STANDARD_DAYS_PER_MONTH;
}

/** Core: (amount ÷ 100) × rupeesPerHundred */
export function rupeeInterestAmount(principal: number, rupeesPerHundred: number): number {
  if (!Number.isFinite(principal) || principal <= 0) return 0;
  if (!Number.isFinite(rupeesPerHundred) || rupeesPerHundred < 0) return 0;
  return (principal / 100) * rupeesPerHundred;
}

export type RupeeInterestBreakdown = {
  principal: number;
  rupeesPerHundred: number;
  basis: InterestBasis;
  interestPerDay: number;
  interestPerMonth: number;
  daysInMonthUsed: number;
  equivalentDailyRate: number;
  equivalentMonthlyRate: number;
};

export function computeRupeeInterestBreakdown(
  principal: number,
  rupeesPerHundred: number,
  basis: InterestBasis,
  refDate = new Date()
): RupeeInterestBreakdown | null {
  if (!Number.isFinite(principal) || principal <= 0) return null;
  if (!Number.isFinite(rupeesPerHundred) || rupeesPerHundred < 0) return null;

  const dim = daysInMonthForDate(refDate);

  if (basis === 'monthly') {
    const interestPerMonth = rupeeInterestAmount(principal, rupeesPerHundred);
    const interestPerDay = interestPerMonth / dim;
    return {
      principal,
      rupeesPerHundred,
      basis,
      interestPerDay,
      interestPerMonth,
      daysInMonthUsed: dim,
      equivalentDailyRate: rupeesPerHundred / dim,
      equivalentMonthlyRate: rupeesPerHundred,
    };
  }

  const interestPerDay = rupeeInterestAmount(principal, rupeesPerHundred);
  const interestPerMonth = interestPerDay * dim;
  return {
    principal,
    rupeesPerHundred,
    basis,
    interestPerDay,
    interestPerMonth,
    daysInMonthUsed: dim,
    equivalentDailyRate: rupeesPerHundred,
    equivalentMonthlyRate: rupeesPerHundred * dim,
  };
}

/** Flat-interest EMI: (principal + total interest over tenure) ÷ months */
export function estimateFlatEmi(
  principal: number,
  rupeesPerHundred: number,
  termMonths: number,
  basis: InterestBasis,
  startDate = new Date()
): number | null {
  if (!Number.isFinite(principal) || principal <= 0) return null;
  if (!Number.isFinite(termMonths) || termMonths <= 0) return null;

  const breakdown = computeRupeeInterestBreakdown(principal, rupeesPerHundred, basis, startDate);
  if (!breakdown) return Math.round(principal / termMonths);

  let totalInterest = 0;
  if (basis === 'monthly') {
    totalInterest = breakdown.interestPerMonth * termMonths;
  } else {
    totalInterest = breakdown.interestPerDay * totalDaysAcrossMonths(termMonths, startDate);
  }

  return Math.round((principal + totalInterest) / termMonths);
}

export function formatInr(amount: number): string {
  return `₹${Math.round(amount).toLocaleString('en-IN')}`;
}

/** @deprecated use InterestBasis */
export type HandLoanInterestBasis = InterestBasis;

export const parseHandLoanInterestBasis = parseInterestBasis;
export const computeHandLoanInterestBreakdown = computeRupeeInterestBreakdown;
export const estimateHandLoanEmi = estimateFlatEmi;
